import React, { useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import LeftPanel from './LeftPanel';
import MainPanel from './MainPanel';
import RightPanel from './RightPanel';
import { getCurrentUser } from './utils/authService';
import { PanelProvider, usePanelContext } from './contexts/PanelContext';
import SettingsModal from './MainPanel/SettingsModal';
import { ThemeProvider } from './contexts/ThemeContext';
import { authFetch } from './utils/authFetch';
import { CategoriesProvider } from './contexts/CategoriesContext';
import { useNotes } from './contexts/NotesContext';
import { StickiesProvider } from './contexts/StickiesContext';
import { EditorProvider } from './contexts/EditorContext';
import styles from './NotesApp.module.css'; // Updated to use module CSS



const NotesAppContent = () => {
  const { 
    notes,
    selectedNoteId,
    selectNote,
    createNote,
    updateNote,
    deleteNote
  } = useNotes();
  
  const { 
    isRightPanelVisible, 
    toggleRightPanel,
    isLeftPanelVisible, 
    toggleLeftPanel
  } = usePanelContext();
  
  // Other state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [activeDock, setActiveDock] = useState('note');
  const [activeContent, setActiveContent] = useState('notes');
  const [isImageUploadExpanded, setImageUploadExpanded] = useState(false);
  const [galleryRefreshTrigger, setGalleryRefreshTrigger] = useState(0);

  // Test auth
  useEffect(() => {
    const testAuth = async () => {
      try {
        console.log('Testing auth...');
        const API_PREFIX = '/api';
        const response = await fetch(`${API_PREFIX}/auth/test-cookie`, {
          credentials: 'include'
        });
        const data = await response.json();
        console.log('Auth test response:', data);
      } catch (error) {
        console.error('Auth test failed:', error);
      }
    };
    
    testAuth();
  }, []);

  // Handlers
  const handleNoteSelect = useCallback((noteId) => {
    selectNote(noteId);
    setActiveDock('note');
  }, [selectNote]);

  const triggerGalleryRefresh = () => {
    console.log("App: triggering gallery refresh");
    setGalleryRefreshTrigger(prev => prev + 1);
  };

  const handleCreateCategory = async (name) => {
    const user = getCurrentUser();
    try {
      const response = await fetch(`/api/users/${user.user_id}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name, is_special: name === 'Main' })
      });

      const newCategory = await response.json();
      
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleDeleteTitle = useCallback(() => {
    console.log('Delete title clicked');
  }, []);

  const handleToggleImageUpload = useCallback(() => setImageUploadExpanded(prev => !prev), []);

  const handleSidebarContentChange = useCallback((content) => {
    console.log('Sidebar content change:', content);
    setActiveContent(content);
  }, []);

  const handleContainerSwap = useCallback(() => {
    console.log('Container swap clicked');
  }, []);

  return (
    <div className={styles.notesAppContainer}>
      <LeftPanel
        onNoteSelect={handleNoteSelect}
        selectedNoteId={selectedNoteId}
        onCreateCategory={handleCreateCategory}
        currentUser={getCurrentUser()}
        notes={notes}
      />
      
      <div className={styles.mainContentWrapper}>
        <MainPanel
          isImageUploadExpanded={isImageUploadExpanded}
          onToggleImageUpload={handleToggleImageUpload}
          activeDock={activeDock}
          setActiveDock={setActiveDock}
          isRightPanelVisible={isRightPanelVisible}
          isLeftPanelVisible={isLeftPanelVisible}
          toggleRightPanel={toggleRightPanel}
          toggleLeftPanel={toggleLeftPanel}
          onDeleteTitle={handleDeleteTitle}
          onSidebarContentChange={handleSidebarContentChange}
          activeContent={activeContent}
          onContainerSwap={handleContainerSwap}
          onOpenSettings={() => setIsSettingsModalOpen(true)}
          onImageUpload={triggerGalleryRefresh}
        />
      </div>
      
      <RightPanel
        isVisible={isRightPanelVisible}
        selectedNoteId={selectedNoteId}
        galleryRefreshTrigger={galleryRefreshTrigger}
      />
      
      <SettingsModal 
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        onSave={(settings) => {
          console.log('Saving settings:', settings);
          setIsSettingsModalOpen(false);
        }}
      />
    </div>
  );
};

const NotesApp = () => {
  // Get the selected note ID from the notes context first
  const NotesContextWrapper = () => {
    const { selectedNoteId } = useNotes();
    
    return (
      <StickiesProvider selectedNoteId={selectedNoteId}>
        <EditorProvider>
          <NotesAppContent />
        </EditorProvider>
      </StickiesProvider>
    );
  };

  return (
    <PanelProvider>
      <CategoriesProvider>
        <ThemeProvider>
          <NotesContextWrapper />
        </ThemeProvider>
      </CategoriesProvider>
    </PanelProvider>
  );
};

export default NotesApp;